import { useState } from 'react';
import { Camera, X, Check, RefreshCw, Sparkles, Receipt, ChevronDown } from 'lucide-react';

interface Props {
  open: boolean;
  onClose: () => void;
}

type Step = 'capture' | 'extracting' | 'review' | 'done';

const mockExtractions = [
  { vendor: 'Bunnings Warehouse', amount: 487.20, gst: 44.29, date: '2026-04-30', category: 'Materials & Tools' },
  { vendor: 'Shell Coles Express', amount: 142.50, gst: 12.95, date: '2026-04-29', category: 'Motor Vehicle' },
  { vendor: 'Officeworks', amount: 89.00, gst: 8.09, date: '2026-04-28', category: 'Office Supplies' },
];

const categories = [
  'Materials & Tools',
  'Motor Vehicle',
  'Office Supplies',
  'Subscriptions',
  'Meals & Entertainment',
  'Travel',
  'Other',
];

export default function ReceiptSnap({ open, onClose }: Props) {
  const [step, setStep] = useState<Step>('capture');
  const [data, setData] = useState(mockExtractions[0]);
  const [pickerOpen, setPickerOpen] = useState(false);

  if (!open) return null;

  const reset = () => {
    setStep('capture');
    setPickerOpen(false);
    setData(mockExtractions[Math.floor(Math.random() * mockExtractions.length)]);
  };

  const handleClose = () => {
    onClose();
    setTimeout(reset, 300);
  };

  const capture = () => {
    setStep('extracting');
    setData(mockExtractions[Math.floor(Math.random() * mockExtractions.length)]);
    setTimeout(() => setStep('review'), 1600);
  };

  const submit = () => {
    setStep('done');
    setTimeout(handleClose, 1500);
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm">
      <div className="absolute inset-x-0 bottom-0 top-12 mx-auto w-full max-w-[560px] overflow-hidden rounded-t-3xl bg-card shadow-2xl">
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <div className="flex items-center gap-2">
            <Receipt className="h-5 w-5 text-foreground" />
            <p className="text-sm font-semibold text-foreground">Snap a receipt</p>
          </div>
          <button
            onClick={handleClose}
            className="flex h-9 w-9 items-center justify-center rounded-full bg-muted text-muted-foreground hover:bg-secondary"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {step === 'capture' && (
          <div className="flex h-[calc(100%-140px)] flex-col items-center justify-center gap-6 p-8">
            <div className="relative flex h-72 w-full max-w-[280px] items-center justify-center overflow-hidden rounded-3xl border-2 border-dashed border-border bg-muted/30">
              <div className="absolute inset-3 rounded-2xl border-2 border-dashed border-primary/40" />
              <div className="text-center">
                <Camera className="mx-auto h-12 w-12 text-muted-foreground" />
                <p className="mt-3 text-sm font-medium text-foreground">Position receipt in frame</p>
                <p className="mt-1 text-xs text-muted-foreground">Hold steady — we'll extract everything</p>
              </div>
            </div>
            <button
              onClick={capture}
              className="flex h-16 w-16 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg hover:bg-primary/90"
            >
              <div className="h-12 w-12 rounded-full border-4 border-primary-foreground/30" />
            </button>
            <p className="text-xs text-muted-foreground">Tap to capture</p>
          </div>
        )}

        {step === 'extracting' && (
          <div className="flex h-[calc(100%-140px)] flex-col items-center justify-center gap-4 p-8">
            <div className="relative">
              <div className="flex h-20 w-20 items-center justify-center rounded-full bg-primary/10">
                <Sparkles className="h-8 w-8 animate-pulse text-primary" />
              </div>
              <div className="absolute inset-0 animate-ping rounded-full border-2 border-primary/40" />
            </div>
            <p className="text-sm font-semibold text-foreground">Reading your receipt…</p>
            <p className="max-w-xs text-center text-xs text-muted-foreground">
              AI is extracting the vendor, amount, GST and category — usually under 2 seconds.
            </p>
          </div>
        )}

        {step === 'review' && (
          <div className="flex h-[calc(100%-140px)] flex-col">
            <div className="border-b border-border bg-emerald-500/5 px-5 py-3">
              <div className="flex items-center gap-2">
                <Sparkles className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />
                <p className="text-xs font-medium text-emerald-700 dark:text-emerald-400">
                  Extracted in 1.4s — review and confirm
                </p>
              </div>
            </div>
            <div className="flex-1 space-y-3 overflow-y-auto px-5 py-4">
              <Field label="Vendor" value={data.vendor} onChange={v => setData({ ...data, vendor: v })} />
              <div className="grid grid-cols-2 gap-3">
                <Field
                  label="Amount (incl. GST)"
                  value={`$${data.amount.toFixed(2)}`}
                  onChange={v => setData({ ...data, amount: parseFloat(v.replace(/[^0-9.]/g, '')) || 0 })}
                />
                <Field
                  label="GST"
                  value={`$${data.gst.toFixed(2)}`}
                  onChange={v => setData({ ...data, gst: parseFloat(v.replace(/[^0-9.]/g, '')) || 0 })}
                />
              </div>
              <Field label="Date" value={data.date} onChange={v => setData({ ...data, date: v })} />

              <div>
                <label className="mb-1.5 block text-xs font-medium text-muted-foreground">Category</label>
                <button
                  onClick={() => setPickerOpen(!pickerOpen)}
                  className="flex w-full items-center justify-between rounded-xl border border-border bg-card px-3.5 py-2.5 text-left text-sm font-medium text-foreground"
                >
                  {data.category}
                  <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${pickerOpen ? 'rotate-180' : ''}`} />
                </button>
                {pickerOpen && (
                  <div className="mt-1 space-y-0.5 rounded-xl border border-border bg-card p-1">
                    {categories.map(c => (
                      <button
                        key={c}
                        onClick={() => { setData({ ...data, category: c }); setPickerOpen(false); }}
                        className={`w-full rounded-lg px-3 py-2 text-left text-sm transition-colors ${
                          data.category === c ? 'bg-primary text-primary-foreground' : 'text-foreground hover:bg-muted'
                        }`}
                      >
                        {c}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="border-t border-border bg-card p-4">
              <div className="grid grid-cols-3 gap-2">
                <button
                  onClick={() => setStep('capture')}
                  className="flex items-center justify-center gap-1.5 rounded-xl bg-muted py-3 text-sm font-medium text-foreground hover:bg-secondary"
                >
                  <RefreshCw className="h-4 w-4" />
                  Retake
                </button>
                <button
                  onClick={submit}
                  className="col-span-2 flex items-center justify-center gap-2 rounded-xl bg-primary py-3 text-sm font-semibold text-primary-foreground hover:bg-primary/90"
                >
                  <Check className="h-4 w-4" />
                  Send to accountant
                </button>
              </div>
            </div>
          </div>
        )}

        {step === 'done' && (
          <div className="flex h-[calc(100%-140px)] flex-col items-center justify-center gap-3 p-8">
            <div className="flex h-20 w-20 items-center justify-center rounded-full bg-emerald-500/15">
              <Check className="h-10 w-10 text-emerald-600 dark:text-emerald-400" />
            </div>
            <p className="text-base font-semibold text-foreground">Sent to your accountant</p>
            <p className="max-w-xs text-center text-xs text-muted-foreground">
              {data.vendor} · ${data.amount.toFixed(2)} added to your records.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <div>
      <label className="mb-1.5 block text-xs font-medium text-muted-foreground">{label}</label>
      <input
        value={value}
        onChange={e => onChange(e.target.value)}
        className="w-full rounded-xl border border-border bg-card px-3.5 py-2.5 text-sm font-medium text-foreground focus:border-ring focus:outline-none"
      />
    </div>
  );
}
